import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { formatDistanceToNow } from "date-fns";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
    ArrowLeft,
    CheckCircle2,
    AlertTriangle,
    AlertCircle,
    RotateCcw,
    Keyboard,
    History,
    CloudOff,
} from "lucide-react";
import { toast } from "sonner";
import { refreshCache, getCache, decideOffline, enqueueScan, getQueue, syncQueue, isNetworkError } from "../../lib/offlineStore";
import { storeGetJSON } from "../../lib/nativeStore";

export default function ScanScreen() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [bus, setBus] = useState(null);
    const [result, setResult] = useState(null); // { result, message, student, attendance }
    const [scanning, setScanning] = useState(false);
    const [manualOpen, setManualOpen] = useState(false);
    const [manualToken, setManualToken] = useState("");
    const [recent, setRecent] = useState([]);
    const [camError, setCamError] = useState("");
    const [pendingQueue, setPendingQueue] = useState([]);
    const [cacheInfo, setCacheInfo] = useState(null);
    const scannerRef = useRef(null);
    const regionId = "qr-reader-region";

    useEffect(() => {
        (async () => {
            const saved = await storeGetJSON("scanix_selected_bus", null);
            if (!saved) {
                navigate("/operator");
                return;
            }
            setBus(saved);
            setPendingQueue(await getQueue());
            setCacheInfo(await getCache());
        })();
    }, [navigate]);

    const loadRecent = async () => {
        try {
            const { data } = await api.get("/attendance", { params: { limit: 10 } });
            setRecent(data);
        } catch {
            // ignore
        }
    };

    useEffect(() => {
        loadRecent();
    }, []);

    // Weak/no signal is the normal case on the client's hill-station routes, not an edge case —
    // keep the local eligibility cache fresh and the offline scan queue draining whenever there
    // happens to be connectivity, without the operator having to do anything.
    const trySync = async () => {
        const cleared = await syncQueue(api);
        if (cleared > 0) {
            setPendingQueue(await getQueue());
            loadRecent();
            toast.success(`Synced ${cleared} offline scan${cleared > 1 ? "s" : ""}`);
        }
    };

    useEffect(() => {
        if (!bus) return;
        refreshCache(api).then(async () => setCacheInfo(await getCache()));
        trySync();

        const onOnline = () => {
            refreshCache(api).then(async () => setCacheInfo(await getCache()));
            trySync();
        };
        window.addEventListener("online", onOnline);
        const interval = setInterval(onOnline, 60000);
        return () => {
            window.removeEventListener("online", onOnline);
            clearInterval(interval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bus]);

    const submitScan = async (token) => {
        if (!bus || !token) return;
        try {
            const { data } = await api.post(
                "/scan",
                { qr_token: token, bus_id: bus.id },
                { timeout: 7000 }
            );
            setResult(data);
            if (data.result === "allowed") toast.success("Allowed");
            else if (data.result === "blocked") toast.warning("Fees Pending");
            else toast.error("Invalid QR");
            loadRecent();
            // A successful call proves we have signal right now — good moment to also flush
            // anything still queued from an earlier dead zone.
            if (pendingQueue.length > 0) trySync();
        } catch (e) {
            if (isNetworkError(e)) {
                const offlineResult = await decideOffline(token, bus, user);
                setResult(offlineResult);
                await enqueueScan(offlineResult.attendance);
                setPendingQueue(await getQueue());
                if (offlineResult.needsReview) toast.warning("Recorded offline — needs review");
                else if (offlineResult.result === "allowed") toast.success("Allowed (offline)");
                else toast.warning("Fees Pending (offline)");
            } else {
                toast.error(e.response?.data?.detail || "Scan failed");
            }
        }
    };

    const startScanner = async () => {
        if (scanning) return;
        setResult(null);
        setCamError("");
        // Tear down any half-started instance first — e.g. a Retry after an
        // error — since acquiring the camera while a prior handle still holds
        // it is exactly what throws NotReadableError below.
        await stopScanner();

        // Pre-flight: secure context + API availability
        if (!window.isSecureContext) {
            setCamError("Camera requires HTTPS. Use the secure preview URL.");
            setManualOpen(true);
            return;
        }
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setCamError("Camera API not supported in this browser.");
            setManualOpen(true);
            return;
        }

        // Note: deliberately NOT doing a separate getUserMedia() pre-flight here
        // (open a stream just to check permission, then stop it) — opening the
        // camera, releasing it, and immediately having html5-qrcode reopen it a
        // moment later is exactly the double-acquire race that throws
        // "NotReadableError: Could not start video source" on many webcams/
        // drivers (the OS hasn't released the device yet). scanner.start()
        // below handles the permission prompt itself.

        // Try to pick a back/rear camera explicitly
        let cameraConfig = { facingMode: "environment" };
        try {
            const devices = await Html5Qrcode.getCameras();
            if (devices && devices.length) {
                const back =
                    devices.find((d) => /back|rear|environment/i.test(d.label)) || devices[devices.length - 1];
                cameraConfig = back.id;
            }
        } catch {
            // fall back to facingMode
        }

        const qrboxFn = (w, h) => {
            const edge = Math.min(w, h, 320);
            const size = Math.max(180, Math.floor(edge * 0.75));
            return { width: size, height: size };
        };

        // "NotReadableError: Could not start video source" is usually a *transient*
        // OS/driver-level lock (the previous holder — another tab, or our own
        // last-used stream — hasn't fully released the device yet), not a real
        // permanent conflict. Retry a couple of times with a short backoff before
        // surfacing an error, instead of failing on the very first attempt.
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                const scanner = new Html5Qrcode(regionId, { verbose: false });
                scannerRef.current = scanner;
                await scanner.start(
                    cameraConfig,
                    { fps: 10, qrbox: qrboxFn, aspectRatio: 1.0 },
                    async (decoded) => {
                        try {
                            await scanner.stop();
                        } catch {
                            // ignore
                        }
                        scannerRef.current = null;
                        setScanning(false);
                        submitScan(decoded);
                    },
                    () => {}
                );
                setScanning(true);
                return;
            } catch (err) {
                scannerRef.current = null;
                const msg = err?.message || String(err);
                const busy = /NotReadableError|Could not start video source/i.test(msg);
                if (busy && attempt < maxAttempts) {
                    await new Promise((r) => setTimeout(r, attempt * 500));
                    continue;
                }
                if (/NotAllowedError|Permission denied/i.test(msg)) {
                    setCamError("Camera permission denied. Please allow camera access and retry.");
                } else if (busy) {
                    setCamError("Camera is busy — another app or browser tab may be using it. Close it and retry.");
                } else if (/NotFoundError|no camera|Requested device not found/i.test(msg)) {
                    setCamError("No camera found on this device.");
                } else {
                    setCamError(`Unable to start camera: ${msg}`);
                }
                toast.error("Camera failed to start");
                setManualOpen(true);
                return;
            }
        }
    };

    const stopScanner = async () => {
        if (scannerRef.current) {
            try {
                await scannerRef.current.stop();
            } catch {
                // ignore
            }
            scannerRef.current = null;
        }
        setScanning(false);
    };

    useEffect(() => {
        return () => {
            stopScanner();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetAndScanAgain = async () => {
        setResult(null);
        await startScanner();
    };

    const submitManual = async (e) => {
        e.preventDefault();
        if (!manualToken.trim()) return;
        setManualOpen(false);
        const t = manualToken.trim();
        setManualToken("");
        await submitScan(t);
    };

    if (!bus) return null;

    return (
        <div className="min-h-screen bg-[#111111] text-white flex flex-col relative">
            {/* Header */}
            <header className="bg-white text-[#111111] px-4 py-3 flex items-center justify-between border-b-2 border-[#111111]">
                <button
                    onClick={() => {
                        stopScanner();
                        navigate("/operator");
                    }}
                    data-testid="back-to-buses"
                    className="flex items-center gap-2 font-bold"
                >
                    <ArrowLeft size={18} />
                    <span className="text-sm">Buses</span>
                </button>
                <div className="text-right">
                    <div className="font-display text-lg leading-none">{bus.bus_name}</div>
                    <div className="text-xs text-[#666666]">{bus.route_name}</div>
                    {pendingQueue.length > 0 && (
                        <div
                            data-testid="pending-sync-badge"
                            className="text-[10px] font-bold text-[#FF9500] flex items-center gap-1 justify-end mt-0.5"
                        >
                            <CloudOff size={11} /> {pendingQueue.length} pending sync
                        </div>
                    )}
                </div>
            </header>

            {/* Scanner area */}
            <div className="flex-1 relative flex items-center justify-center overflow-hidden">
                <div id={regionId} className="absolute inset-0 bg-black" />

                {!scanning && !result && (
                    <div className="relative z-20 text-center px-6">
                        <div className="w-20 h-20 bg-[#FF3B30] mx-auto flex items-center justify-center mb-6 animate-pulse-red">
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
                                <rect x="7" y="7" width="10" height="10" />
                            </svg>
                        </div>
                        <h2 className="font-display text-3xl mb-2">Ready to scan</h2>
                        <p className="text-white/60 mb-8 text-sm">
                            Tap below to open the camera and scan a student QR code.
                        </p>
                        {!camError && cacheInfo?.generatedAt && (
                            <p className="text-white/40 text-xs mb-6">
                                Offline data cached {formatDistanceToNow(new Date(cacheInfo.generatedAt), { addSuffix: true })}
                            </p>
                        )}
                        {camError && (
                            <div
                                data-testid="camera-error"
                                className="mb-6 bg-[#FF3B30]/15 border border-[#FF3B30]/40 text-[#FF3B30] px-4 py-3 text-sm max-w-sm mx-auto"
                            >
                                {camError}
                            </div>
                        )}
                        <button
                            onClick={startScanner}
                            data-testid="start-scan-button"
                            className="bg-[#FF3B30] text-white font-bold px-8 py-4 hover:bg-[#D70015]"
                        >
                            {camError ? "Retry Camera" : "Open Scanner"}
                        </button>
                        <div className="mt-8 flex items-center justify-center gap-4">
                            <button
                                onClick={() => setManualOpen(true)}
                                data-testid="manual-entry-button"
                                className="text-white/70 text-sm flex items-center gap-2 hover:text-white"
                            >
                                <Keyboard size={14} /> Enter token manually
                            </button>
                        </div>
                    </div>
                )}

                {scanning && (
                    <div className="absolute inset-0 z-10 pointer-events-none">
                        {/* 4-sided dim overlay — clear 256×256 hole in the center */}
                        <div
                            className="absolute left-0 right-0 top-0 bg-black/70"
                            style={{ height: "calc(50% - 128px)" }}
                        />
                        <div
                            className="absolute left-0 right-0 bottom-0 bg-black/70"
                            style={{ height: "calc(50% - 128px)" }}
                        />
                        <div
                            className="absolute left-0 bg-black/70"
                            style={{
                                top: "calc(50% - 128px)",
                                bottom: "calc(50% - 128px)",
                                width: "calc(50% - 128px)",
                            }}
                        />
                        <div
                            className="absolute right-0 bg-black/70"
                            style={{
                                top: "calc(50% - 128px)",
                                bottom: "calc(50% - 128px)",
                                width: "calc(50% - 128px)",
                            }}
                        />
                        {/* Reticle — centered, transparent interior */}
                        <div
                            className="absolute w-64 h-64"
                            style={{
                                left: "50%",
                                top: "50%",
                                transform: "translate(-50%, -50%)",
                            }}
                        >
                            <span className="reticle-corner tl" />
                            <span className="reticle-corner tr" />
                            <span className="reticle-corner bl" />
                            <span className="reticle-corner br" />
                            <div className="absolute left-0 right-0 h-0.5 bg-[#FF3B30] animate-scan-line" />
                        </div>
                        <div className="absolute bottom-24 left-0 right-0 text-center text-white/80 text-sm">
                            Align the QR within the frame
                        </div>
                        <div className="absolute bottom-8 left-0 right-0 flex justify-center pointer-events-auto">
                            <button
                                onClick={stopScanner}
                                data-testid="cancel-scan"
                                className="bg-white text-[#111111] font-bold px-6 py-3"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}

                {result && (
                    <ResultSheet
                        result={result}
                        onScanAgain={resetAndScanAgain}
                        onManualEntry={() => {
                            setResult(null);
                            setManualOpen(true);
                        }}
                    />
                )}
            </div>

            {/* Footer — recent scans toggle. Locally-queued offline scans aren't in the server's
                /attendance response yet, so merge them in ahead of it (most recent first). */}
            <RecentScansDrawer
                recent={[
                    ...pendingQueue
                        .slice()
                        .reverse()
                        .map((q) => ({
                            id: q.client_scan_id,
                            student_name: q.student_name || "Unknown",
                            scan_time: q.scanned_at,
                            result: q.result,
                            pending: true,
                        })),
                    ...recent,
                ].slice(0, 15)}
            />

            {/* Manual entry modal */}
            {manualOpen && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <div className="bg-white text-[#111111] border-2 border-[#111111] w-full max-w-sm">
                        <div className="px-5 py-3 border-b-2 border-[#111111] font-display text-xl">
                            Manual Token Entry
                        </div>
                        <form onSubmit={submitManual} className="p-5 space-y-4">
                            <input
                                autoFocus
                                data-testid="manual-token-input"
                                value={manualToken}
                                onChange={(e) => setManualToken(e.target.value)}
                                placeholder="SCX-…"
                                className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-3 font-mono focus:border-[#111111] outline-none"
                            />
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setManualOpen(false)}
                                    className="flex-1 border border-[#111111] font-bold py-3"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    data-testid="manual-submit"
                                    className="flex-1 bg-[#FF3B30] text-white font-bold py-3"
                                >
                                    Verify
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

function ResultSheet({ result, onScanAgain, onManualEntry }) {
    const isAllowed = result.result === "allowed";
    const isBlocked = result.result === "blocked";
    // An offline "allowed" that only happened because the QR wasn't in the cached student list
    // (fail-open, per policy) gets its own amber treatment — the outcome is "let them board",
    // but the operator should still see this wasn't actually verified.
    const isUnverified = result.offline && result.needsReview;
    const color = isUnverified ? "#FF9500" : isAllowed ? "#34C759" : isBlocked ? "#FF9500" : "#666666";
    const Icon = isUnverified ? AlertCircle : isAllowed ? CheckCircle2 : isBlocked ? AlertTriangle : AlertCircle;
    const heading = isUnverified
        ? "Allowed — Unverified"
        : isAllowed
        ? "Boarding Approved"
        : isBlocked
        ? "Fees Pending"
        : "Invalid QR";
    return (
        <div
            data-testid="scan-result-sheet"
            className="absolute inset-0 z-30 flex flex-col animate-slide-up"
            style={{ backgroundColor: color, color: "#FFFFFF" }}
        >
            {result.offline && (
                <div
                    data-testid="offline-banner"
                    className="bg-black/20 text-center text-xs font-bold uppercase tracking-wider py-2 flex items-center justify-center gap-2"
                >
                    <CloudOff size={13} /> Offline — saved on device, will sync
                </div>
            )}
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
                <Icon size={88} strokeWidth={2.4} />
                <div className="label-caps mt-6" style={{ color: "rgba(255,255,255,0.85)" }}>
                    {result.result}
                </div>
                <h2 className="font-display text-5xl mt-2">{heading}</h2>
                <p className="text-lg mt-3 max-w-sm" style={{ color: "rgba(255,255,255,0.9)" }}>
                    {result.message}
                </p>
                {result.student && (
                    <div className="mt-8 bg-white/15 border border-white/30 px-6 py-4 w-full max-w-sm">
                        <div className="font-display text-2xl">{result.student.name}</div>
                        <div className="font-mono text-sm" style={{ color: "rgba(255,255,255,0.8)" }}>
                            {result.student.roll_number} · {result.student.department}
                        </div>
                    </div>
                )}
            </div>
            <div className="p-5 space-y-3">
                <button
                    onClick={onScanAgain}
                    data-testid="scan-again-button"
                    className="w-full bg-white font-bold py-4 flex items-center justify-center gap-2"
                    style={{ color }}
                >
                    <RotateCcw size={18} /> Scan Next Student
                </button>
                <button
                    onClick={onManualEntry}
                    data-testid="result-manual-entry"
                    className="w-full bg-transparent text-white border border-white/50 font-bold py-3 flex items-center justify-center gap-2"
                >
                    <Keyboard size={16} /> Enter Token Manually
                </button>
            </div>
        </div>
    );
}

function RecentScansDrawer({ recent }) {
    const [open, setOpen] = useState(false);
    return (
        <div
            className={`bg-white text-[#111111] border-t-2 border-[#111111] transition-all z-20 ${
                open ? "h-80" : "h-14"
            }`}
        >
            <button
                onClick={() => setOpen(!open)}
                data-testid="recent-toggle"
                className="w-full px-5 h-14 flex items-center justify-between font-bold"
            >
                <span className="flex items-center gap-2">
                    <History size={16} /> Recent Scans
                </span>
                <span className="text-xs text-[#666666]">
                    {recent.length} · tap to {open ? "hide" : "view"}
                </span>
            </button>
            {open && (
                <div className="overflow-y-auto h-[calc(100%-3.5rem)]" data-testid="recent-scans-list">
                    {recent.length === 0 && (
                        <div className="p-4 text-center text-[#666666] text-sm">No recent scans</div>
                    )}
                    {recent.map((r) => (
                        <div
                            key={r.id}
                            className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between"
                        >
                            <div>
                                <div className="font-medium text-sm">{r.student_name}</div>
                                <div className="text-xs text-[#666666] font-mono">
                                    {new Date(r.scan_time).toLocaleTimeString()}
                                </div>
                            </div>
                            <span
                                className={`text-xs font-bold uppercase tracking-wider px-2 py-1 border ${
                                    r.pending
                                        ? "bg-[#FF9500]/10 text-[#FF9500] border-[#FF9500]/30 animate-pulse"
                                        : r.result === "allowed"
                                        ? "bg-[#34C759]/10 text-[#34C759] border-[#34C759]/30"
                                        : r.result === "blocked"
                                        ? "bg-[#FF9500]/10 text-[#FF9500] border-[#FF9500]/30"
                                        : "bg-[#666666]/10 text-[#666666] border-[#666666]/30"
                                }`}
                            >
                                {r.pending ? "Pending Sync" : r.result}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
